import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, MapPin, Loader } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Cart = () => {
  const { cart, updateQuantity, removeFromCart, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState('cart'); // 'cart', 'shipping'
  const [error, setError] = useState(null);
  const [shippingAddress, setShippingAddress] = useState({
    full_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    phone: ''
  });

  const fetchProductDetails = useCallback(async () => {
    if (cart.length === 0) return;
    
    setLoading(true);
    setError(null);
    try {
      const productIds = cart.map(item => item.product_id);
      const productPromises = productIds.map(id => axios.get(`${API_URL}/products/${id}`));
      const responses = await Promise.all(productPromises);
      
      const productsMap = {};
      responses.forEach(response => {
        productsMap[response.data.id] = response.data;
      });
      setProducts(productsMap);
    } catch (err) {
      setError('Unable to load product details. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  }, [cart]);

  useEffect(() => {
    fetchProductDetails();
  }, [fetchProductDetails]);

  const calculateTotal = () => {
    return cart.reduce((total, item) => {
      const product = products[item.product_id];
      return total + (product ? product.price * item.quantity : 0);
    }, 0);
  };

  const handleCheckout = () => {
    if (!user) {
      toast.error('Please login to checkout');
      // Store redirect destination so user comes back to cart after login
      navigate('/login?redirect=/cart&checkout=true');
      return;
    }

    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    // Show shipping address form first
    setCheckoutStep('shipping');
  };

  const handleShippingSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!shippingAddress.full_name || !shippingAddress.address_line1 || 
        !shippingAddress.city || !shippingAddress.state || !shippingAddress.zip_code) {
      toast.error('Please fill in all required shipping fields');
      return;
    }
    
    // Proceed to Stripe checkout
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API_URL}/checkout`, {
        origin_url: window.location.origin,
        shipping_address: shippingAddress
      });

      if (response.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout session. Please try again.');
      setProcessingPayment(false);
    }
  };

  const handleShippingChange = (e) => {
    const { name, value } = e.target;
    setShippingAddress(prev => ({ ...prev, [name]: value }));
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading cart...</div>;
  }

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ShoppingBag className="w-24 h-24 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-heading font-semibold mb-2">Your cart is empty</h2>
          <p className="text-gray-600 mb-6">Add some products to get started</p>
          <Link to="/shop">
            <Button className="bg-medical-primary hover:bg-medical-primary/90">Shop Now</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-heading font-semibold mb-8">Shopping Cart</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              {cart.map((item) => {
                const product = products[item.product_id];
                if (!product) return null;

                return (
                  <div key={item.product_id} className="p-6 border-b border-gray-200 last:border-b-0" data-testid={`cart-item-${item.product_id}`}>
                    <div className="flex gap-4">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <Link to={`/product/${product.id}`} className="font-heading font-medium text-lg hover:text-medical-primary">
                          {product.name}
                        </Link>
                        <p className="text-sm text-gray-600 mt-1">{product.category}</p>
                        <p className="text-medical-primary font-semibold mt-2">${product.price.toFixed(2)}</p>
                      </div>
                      <div className="flex flex-col items-end justify-between">
                        <button
                          onClick={() => removeFromCart(item.product_id)}
                          className="text-red-600 hover:text-red-700 p-2"
                          data-testid="remove-item-button"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                        <div className="flex items-center border border-gray-300 rounded-md">
                          <button
                            onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                            className="p-2 hover:bg-gray-100"
                            data-testid="decrease-cart-quantity"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="px-4" data-testid="cart-item-quantity">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                            className="p-2 hover:bg-gray-100"
                            data-testid="increase-cart-quantity"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-6 sticky top-24">
              <h2 className="font-heading font-semibold text-xl mb-4">Order Summary</h2>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span data-testid="cart-subtotal">${calculateTotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Shipping</span>
                  <span className="text-green-600">FREE</span>
                </div>
                <div className="border-t pt-3 flex justify-between font-semibold text-lg">
                  <span>Total</span>
                  <span className="text-medical-primary" data-testid="cart-total">${calculateTotal().toFixed(2)}</span>
                </div>
              </div>

              <Button
                onClick={handleCheckout}
                className="w-full bg-medical-primary hover:bg-medical-primary/90"
                size="lg"
                data-testid="checkout-button"
              >
                Proceed to Checkout
              </Button>

              <Link to="/shop" className="block text-center text-medical-primary mt-4 hover:underline">
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>

        {/* Shipping Address Dialog */}
        <Dialog open={checkoutStep === 'shipping'} onOpenChange={(open) => {
          if (!open) setCheckoutStep('cart');
        }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-medical-primary" />
                Shipping Address
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleShippingSubmit} className="space-y-4 py-4">
              <div>
                <label className="block text-sm font-medium mb-1">Full Name *</label>
                <Input
                  name="full_name"
                  value={shippingAddress.full_name}
                  onChange={handleShippingChange}
                  placeholder="John Doe"
                  required
                  data-testid="shipping-full-name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Address Line 1 *</label>
                <Input
                  name="address_line1"
                  value={shippingAddress.address_line1}
                  onChange={handleShippingChange}
                  placeholder="123 Main Street"
                  required
                  data-testid="shipping-address1"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Address Line 2 (Optional)</label>
                <Input
                  name="address_line2"
                  value={shippingAddress.address_line2}
                  onChange={handleShippingChange}
                  placeholder="Apt, Suite, Unit, etc."
                  data-testid="shipping-address2"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">City *</label>
                  <Input
                    name="city"
                    value={shippingAddress.city}
                    onChange={handleShippingChange}
                    placeholder="Orlando"
                    required
                    data-testid="shipping-city"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">State *</label>
                  <Input
                    name="state"
                    value={shippingAddress.state}
                    onChange={handleShippingChange}
                    placeholder="FL"
                    required
                    data-testid="shipping-state"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">ZIP Code *</label>
                  <Input
                    name="zip_code"
                    value={shippingAddress.zip_code}
                    onChange={handleShippingChange}
                    placeholder="32801"
                    required
                    data-testid="shipping-zip"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Phone (Optional)</label>
                  <Input
                    name="phone"
                    value={shippingAddress.phone}
                    onChange={handleShippingChange}
                    placeholder="(407) 555-1234"
                    data-testid="shipping-phone"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCheckoutStep('cart')}
                  className="flex-1"
                  disabled={processingPayment}
                >
                  Back to Cart
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-medical-primary hover:bg-medical-primary/90"
                  data-testid="continue-to-payment"
                  disabled={processingPayment}
                >
                  {processingPayment ? (
                    <span className="flex items-center justify-center">
                      <Loader className="w-4 h-4 animate-spin mr-2" />
                      Processing...
                    </span>
                  ) : (
                    'Proceed to Payment'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Cart;